// Pure, portable 13-week cash runway for the Profit First tab.
//
// Tracks the Opex + Vault pool (the buffer pool) day by day against the firm's
// real outflow schedule: employee payroll on the 15th and last business day,
// contractors every Friday, referral fees by ACH mid-month, and the credit
// card in full on its due day. Owner's pay and taxes draw from their own
// accounts, so they are NOT in this pool. Profit is carried separately as a
// "break-glass" failsafe band, never folded into the buffer.
//
// No DB, no React, no app imports. Verified by verifyRunway.mjs.

function isWeekend(d) {
  const g = d.getDay();
  return g === 0 || g === 6;
}

// Last business day (Mon-Fri) of the given month.
export function lastBusinessDay(year, monthIdx) {
  const d = new Date(year, monthIdx + 1, 0);
  while (isWeekend(d)) d.setDate(d.getDate() - 1);
  return d.getDate();
}

// How many Mondays fall in a calendar month (never 0, so income always spreads).
export function mondaysInMonth(year, monthIdx) {
  const last = new Date(year, monthIdx + 1, 0).getDate();
  let count = 0;
  for (let day = 1; day <= last; day++) {
    if (new Date(year, monthIdx, day).getDay() === 1) count += 1;
  }
  return count || 1;
}

export const DEFAULT_SCHEDULE = {
  payrollDays: [15, 'last'], // 15th and the last business day
  contractorDow: 5,          // Friday (0=Sun … 6=Sat)
  referralDay: 20,           // mid-month ACH
  cardDay: 7,                // card paid in full
};

/**
 * Compute the pool runway.
 *
 * @param {Object} p
 * @param {Date|string} p.start           first day of the runway
 * @param {number}      [p.weeks=13]      horizon in weeks
 * @param {Object}      p.monthlyIncome   calendar-month index (0-11) -> total income $
 * @param {number}      [p.opexCapPct=0.65] share of each Monday deposit routed to Opex
 * @param {Object}      p.outflows        { employeePayrollPerRun, contractorPerWeek, referralMonthly, cardMonthly }
 * @param {Object}      [p.schedule]      overrides for DEFAULT_SCHEDULE
 * @param {Object}      p.balances        { opex, vault, profit }
 * @param {number}      p.buffer          the one-month floor
 * @returns {{poolStart:number, failsafe:number, daily:Array, weeks:Array, low:{pool:number,date:Date}, safeToSweep:number}}
 */
export function computeRunway(p) {
  const {
    start, weeks = 13, monthlyIncome = {}, opexCapPct = 0.65,
    outflows, schedule, balances, buffer = 0,
  } = p;
  const sch = { ...DEFAULT_SCHEDULE, ...(schedule || {}) };
  const of = {
    employeePayrollPerRun: 0, contractorPerWeek: 0, referralMonthly: 0, cardMonthly: 0,
    ...(outflows || {}),
  };
  const poolStart = (balances?.opex || 0) + (balances?.vault || 0);
  const failsafe = balances?.profit || 0;

  const S = new Date(start);
  S.setHours(0, 0, 0, 0);
  let running = poolStart;
  const daily = [{ date: new Date(S), pool: running }];
  const wk = [];
  const low = { pool: Infinity, date: null };

  for (let w = 0; w < weeks; w++) {
    const wkStart = new Date(S);
    wkStart.setDate(S.getDate() + w * 7);
    let wIn = 0, wPay = 0, wCon = 0, wRef = 0, wCard = 0;
    for (let d = 0; d < 7; d++) {
      const day = new Date(wkStart);
      day.setDate(wkStart.getDate() + d);
      const dow = day.getDay(), dom = day.getDate(), mo = day.getMonth(), yr = day.getFullYear();

      // Income posts on Mondays; each month's income spreads across its Mondays.
      if (dow === 1) {
        const inc = (monthlyIncome[mo] || 0) / mondaysInMonth(yr, mo);
        const opexIn = inc * opexCapPct;
        running += opexIn; wIn += opexIn;
      }
      // Employee payroll on the 15th and last business day.
      const payDay = sch.payrollDays.some((pd) => (pd === 'last' ? dom === lastBusinessDay(yr, mo) : dom === pd));
      if (payDay) { running -= of.employeePayrollPerRun; wPay += of.employeePayrollPerRun; }
      // Contractors every Friday.
      if (dow === sch.contractorDow) { running -= of.contractorPerWeek; wCon += of.contractorPerWeek; }
      // Referral ACH mid-month.
      if (dom === sch.referralDay) { running -= of.referralMonthly; wRef += of.referralMonthly; }
      // Card in full on its due day.
      if (dom === sch.cardDay) { running -= of.cardMonthly; wCard += of.cardMonthly; }

      daily.push({ date: new Date(day), pool: running });
      if (running < low.pool) { low.pool = running; low.date = new Date(day); }
    }
    wk.push({ date: new Date(wkStart), in: wIn, pay: wPay, con: wCon, ref: wRef, card: wCard, end: running });
  }

  const safeToSweep = Math.max(0, low.pool - buffer);
  return { poolStart, failsafe, daily, weeks: wk, low, safeToSweep };
}

// Convenience: the one-month buffer floor from the outflow figures
// (employee salary + contractors + referral + card).
export function bufferFromOutflows(of) {
  return (
    (of.employeePayrollPerRun || 0) * 2 +
    (of.contractorPerWeek || 0) * 4.33 +
    (of.referralMonthly || 0) +
    (of.cardMonthly || 0)
  );
}
